<template>
  <UModal
    ref="modal"
    v-model:open="isOpened"
    :class="ui?.root"
    :ui="{
      header: 'hidden',
      content: 'feedback-modal',
      close: 'absolute',
      overlay: 'bg-overlay',
    }"
  >
    <template #body>
      <UButton
        class="close-button"
        variant="outline"
        icon="i-local-close"
        color="neutral"
        size="sm"
        @click="closeModal"
      />

      <div
        class="wrapper rounded-2xl"
        :class="ui?.wrapper"
        :style="{
          backgroundImage: `url(${img('/images/modal/football-grass.jpg', {
            width: 1000,
          })})`,
        }"
      >
        <div class="content" :class="ui?.content">
          <div class="title" :class="ui?.title">
            Есть предложения по развитию или сотрудничеству?
          </div>
          <div class="description" :class="ui?.description">
            Напишите нам и мы свяжемся с вами в ближайшее время
          </div>

          <UForm
            v-model="form"
            class="mt-6 w-full gap-4 space-y-4"
            :class="ui?.form"
            @submit="onSubmit"
          >
            <UInput
              v-model="form.name"
              name="name"
              class="w-full"
              placeholder="Ваше имя *"
              required
              type="text"
              :ui="{
                base: 'h-12!',
              }"
            />
            <UInput
              v-model="form.phone"
              v-maska="'+7 (###) ###-##-##'"
              name="phone"
              class="w-full"
              placeholder="Телефон *"
              required
              :ui="{
                base: 'h-12!',
              }"
            />
            <UTextarea
              v-model="form.message"
              class="w-full h-22"
              placeholder="Оставьте сообщение"
              :autoresize="false"
              name="message"
              size="xl"
            />

            <UCheckbox
              name="agree"
              class="mb-6 agree-checkbox"
              required
              :ui="{
                label: 'typo-paragraph-xs-regular',
              }"
            >
              <template #label>
                Я ознакомлен(-а) с <a
                  class="text-link!"
                  href="/documents/personal-data"
                  target="_blank"
                  rel="noopener noreferrer"
                  >Политикой обработки персональных данных</a
                >
                и даю своё
                <a
                  class="text-link!"
                  href="/documents/personal-data"
                  target="_blank"
                  rel="noopener noreferrer"
                  >согласие на обработку персональных данных</a
                >.
              </template>
            </UCheckbox>

            <UButton
              type="submit"
              class="h-12 w-full justify-center"
              :class="ui?.submitButton"
            >
              Отправить
            </UButton>
          </UForm>
        </div>
      </div>
    </template>

    <slot></slot>
  </UModal>
</template>

<script lang="ts" setup>
import { vMaska } from 'maska/vue'
import { successModal } from '~/components/modals'

defineOptions({
  name: 'FeedbackModal',
})

const img = useImage()

const emit = defineEmits<{
  close: []
}>()

defineProps<{
  ui?: {
    root?: string
    wrapper?: string
    content?: string
    title?: string
    description?: string
    form?: string
    submitButton?: string
  }
}>()

const isOpened = ref(false)

const form = reactive({
  name: '',
  phone: '',
  message: '',
})

const onSubmit = () => {
  isOpened.value = false
  successModal.open({
    title: 'Ваше сообщение отправлено',
    description:
      'Спасибо за обращение, мы свяжемся с вами в ближайшее рабочее время',
  })
}

const closeModal = () => {
  isOpened.value = false
  emit('close')
}
</script>

<style lang="scss">
.feedback-modal {
  --tw-ring-color: transparent;
  width: 940px;
  max-width: 940px;
  height: 528px;
  border-radius: 16px;
  border-color: transparent;
  --tw-shadow:
    0 10px 15px -3px var(--tw-shadow-color, rgb(0 0 0 / 0.1)),
    0 4px 6px -4px var(--tw-shadow-color, rgb(0 0 0 / 0.1));
  box-shadow:
    var(--tw-inset-shadow), var(--tw-inset-ring-shadow),
    var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow);

  @include max-lg {
    height: 720px;
    max-width: 540px;
  }

  @include max-md {
    max-width: 100dvw;
    top: unset;
    bottom: 0;
    left: 0;
    translate: none;
    border-radius: 0;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }

  [data-slot='body'] {
    position: relative;
    border: none;
    border-color: unset;
    border-bottom-color: unset;
    padding: 0;
    border-radius: 16px;

    @include max-md {
      border-radius: 0;
    }
  }

  .agree-checkbox {
    @include typo('paragraph-xs-regular');
  }
}
</style>

<style lang="scss" scoped>
.close-button {
  position: absolute;
  top: 24px;
  right: 24px;

  @include max-md {
    top: 16px;
    right: 16px;
  }
}

.wrapper {
  height: 100%;
  background-size: cover;
  background-position: 250px;
  border-radius: 16px;

  @include max-lg {
    background-position: 0 -85px;
    background-size: contain;
    background-repeat: no-repeat;
  }

  @include max-md {
    border-radius: 0;
  }
}

.content {
  padding: 32px;
  width: 539px;
  max-width: 539px;
  height: 100%;
  background-color: var(--color-white);
  border-radius: 16px;
  overflow-y: auto;

  @include max-lg {
    position: relative;
    top: 204px;
    width: 100%;
    max-width: 100%;
    height: unset;
  }

  @include max-md {
    top: 184px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.title {
  @include typo('heading-h3');
  color: var(--color-modal-title);
  margin-bottom: 10px;
  letter-spacing: -0.8px;
}

.description {
  @include typo('paragraph-sm-regular');
  color: var(--color-content-default-secondary);
}

a {
  color: var(--color-accent-default);
}
</style>
